/**
 * Asset library for the toon renderer. Loads the KayKit .glb files and the hero .vrm files
 * once (module-level promise) and hands out clones. Failures are logged and recorded; callers
 * fall back to placeholder capsules or procedural props, never a blank scene.
 */
import * as THREE from 'three';
import { type GLTF, GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { type VRM, VRMLoaderPlugin, VRMUtils } from '@pixiv/three-vrm';
import { addOutlineNormals } from './style';
import { HERO_VRM } from './heroModels';
import { VENOM_KITS, VENOM_TEXTURES } from './themes/venomAltar/manifest';

export const KAYKIT_FILES = [
  'Skeleton_Warrior',
  'Skeleton_Minion',
  'Skeleton_Mage',
  'Skeleton_Axe',
  'Skeleton_Blade',
  'Skeleton_Staff',
  'Skeleton_Shield_Large_A',
  'Knight',
  'Mage',
  'Barbarian', // +field: bandits, the smith
  'Rogue_Hooded', // +field: bandit slingers, villagers
  'wall',
  'wall_doorway',
  'pillar_decorated',
  'column',
  'crates_stacked',
  'box_large',
  'barrel_large',
  'rubble_large',
  'rubble_half',
  'torch_lit',
  'banner_patternA_red',
] as const;
export type KayKitFile = (typeof KAYKIT_FILES)[number];

// Hero VRM per class id (role fallback): a three-free module so meta code (and the Worker) can use it.
export { HERO_VRM, ROLE_VRM } from './heroModels';
const VRM_FILES = [...new Set(Object.values(HERO_VRM))];

const base = (): string => (import.meta.env?.BASE_URL ?? '/').replace(/\/?$/, '/');

export interface VrmSlot {
  vrm: VRM;
  /** Set while a renderer has it in its scene (a VRM can't be cloned, so one live user at a time). */
  inUse: boolean;
  /** Extra objects attached at prep time (weapons) — survive across runs. */
  prepared: boolean;
  /** Outline hulls added at prep time (toggled by settings). */
  hulls: THREE.Object3D[];
  /** +1 when the normalized rig's left arm points +X (model faces +Z in rig space), else -1. */
  side: number;
  /** +1 when a negative x rotation swings a limb toward the face in rig space, else -1. */
  fz: number;
  /** Right/left hand bones (raw) for glows. */
  rightHand: THREE.Object3D | null;
  /** Weapons/orb attached under the raw hand bones at prep time (their world matrices follow each frame). */
  weaponHolders: THREE.Object3D[];
  /** Finger curl per hand (1 = fist around a handle). */
  fist: { left: number; right: number };
}

export class ToonAssets {
  readonly gltf = new Map<string, GLTF>();
  /** Theme prop kits (one GLB per pack, props as named top-level nodes): 'kd' | 'kh' | 'kn'. */
  readonly kits = new Map<string, GLTF>();
  /** Theme textures by asset id (tiling, sRGB, repeat-wrapped). */
  readonly textures = new Map<string, THREE.Texture>();
  private readonly kitGeo = new Map<string, THREE.BufferGeometry | null>();
  private atlas: THREE.Texture | null | undefined;
  readonly vrmBuffers = new Map<string, ArrayBuffer>();
  readonly vrms = new Map<string, VrmSlot[]>();
  readonly failed = new Set<string>();
  private vrmLoader: GLTFLoader;

  constructor() {
    this.vrmLoader = new GLTFLoader();
    this.vrmLoader.register((parser) => new VRMLoaderPlugin(parser));
  }

  has(name: string): boolean {
    return this.gltf.has(name);
  }

  /** Deep clone (SkeletonUtils for skinned rigs). Shares geometry/materials with the source. */
  clone(name: string): THREE.Object3D | null {
    const g = this.gltf.get(name);
    if (!g) return null;
    const c = SkeletonUtils.clone(g.scene);
    shareSkeletons(c);
    return c;
  }

  clips(name: string): THREE.AnimationClip[] {
    return this.gltf.get(name)?.animations ?? [];
  }

  /** First mesh of a single-mesh prop (for instancing), with its world transform baked relative to the root. */
  propMeshes(name: string): { geometry: THREE.BufferGeometry; material: THREE.Material; matrix: THREE.Matrix4 }[] {
    const g = this.gltf.get(name);
    if (!g) return [];
    g.scene.updateMatrixWorld(true);
    const out: { geometry: THREE.BufferGeometry; material: THREE.Material; matrix: THREE.Matrix4 }[] = [];
    g.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) out.push({ geometry: m.geometry, material: (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material, matrix: m.matrixWorld.clone() });
    });
    return out;
  }

  /** Theme texture by asset id (shared; never disposed per run). */
  texture(id: string): THREE.Texture | null {
    return this.textures.get(id) ?? null;
  }

  /**
   * One shared texture for every theme kit prop: the Dungeon and Halloween atlases side by side
   * plus a white strip for flat-coloured (Kenney) and procedural props, so a whole room
   * cluster's props merge into one draw. flipY = false (glTF convention). Null without a DOM.
   */
  propAtlas(): THREE.Texture | null {
    if (this.atlas !== undefined) return this.atlas;
    this.atlas = null;
    if (typeof document === 'undefined') return null;
    const kd = kitImage(this.kits.get('kd'));
    const kh = kitImage(this.kits.get('kh'));
    const cv = document.createElement('canvas');
    cv.width = ATLAS_W;
    cv.height = ATLAS_H;
    const g = cv.getContext('2d');
    if (!g) return null;
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, ATLAS_W, ATLAS_H);
    if (kd) g.drawImage(kd, 0, 0, ATLAS_CELL, ATLAS_CELL);
    if (kh) g.drawImage(kh, ATLAS_CELL, 0, ATLAS_CELL, ATLAS_CELL);
    const tex = new THREE.CanvasTexture(cv);
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    tex.name = 'propAtlas';
    this.atlas = tex;
    return tex;
  }

  /**
   * A theme kit prop as one geometry ready for merging: position, normal, outlineNormal,
   * uv (remapped into propAtlas) and color (material colour for flat Kenney parts, else white).
   * Cached; null when the kit or node is missing.
   */
  kitGeometry(id: string): THREE.BufferGeometry | null {
    if (this.kitGeo.has(id)) return this.kitGeo.get(id)!;
    const pack = id.slice(0, 2);
    const kit = this.kits.get(pack);
    const node = kit?.scene.getObjectByName(id);
    let out: THREE.BufferGeometry | null = null;
    if (node) {
      node.updateWorldMatrix(true, true);
      const inv = new THREE.Matrix4().copy(node.matrixWorld).invert();
      const parts: THREE.BufferGeometry[] = [];
      node.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        const src = m.geometry;
        const groups = src.groups.length ? src.groups : [{ start: 0, count: src.index ? src.index.count : (src.attributes['position'] as THREE.BufferAttribute).count, materialIndex: 0 }];
        for (const grp of groups) {
          const mat = mats[grp.materialIndex ?? 0] as THREE.MeshStandardMaterial;
          let g = src.clone();
          if (src.groups.length > 1) {
            const idx = src.index!;
            const sub = new Uint32Array(grp.count);
            for (let i = 0; i < grp.count; i++) sub[i] = idx.getX(grp.start + i);
            g.setIndex(new THREE.BufferAttribute(sub, 1));
          }
          g = g.index ? g.toNonIndexed() : g;
          g.clearGroups();
          g.applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld));
          const n = (g.attributes['position'] as THREE.BufferAttribute).count;
          const uv = new Float32Array(n * 2);
          const srcUv = g.attributes['uv'] as THREE.BufferAttribute | undefined;
          const hasMap = !!mat.map && !!srcUv;
          const [ox, oy] = pack === 'kh' ? [0.5, 0] : [0, 0];
          for (let i = 0; i < n; i++) {
            if (hasMap) {
              uv[i * 2] = ox + srcUv!.getX(i) * 0.5;
              uv[i * 2 + 1] = oy + srcUv!.getY(i) * (ATLAS_CELL / ATLAS_H);
            } else {
              uv[i * 2] = ATLAS_WHITE[0];
              uv[i * 2 + 1] = ATLAS_WHITE[1];
            }
          }
          const col = new Float32Array(n * 3);
          const c = hasMap ? new THREE.Color(1, 1, 1) : (mat.color?.clone() ?? new THREE.Color(1, 1, 1));
          if (!hasMap) recolorKenney(mat.name, c);
          for (let i = 0; i < n; i++) {
            col[i * 3] = c.r;
            col[i * 3 + 1] = c.g;
            col[i * 3 + 2] = c.b;
          }
          const clean = new THREE.BufferGeometry();
          clean.setAttribute('position', g.attributes['position']!);
          if (!g.attributes['normal']) g.computeVertexNormals();
          clean.setAttribute('normal', g.attributes['normal']!);
          clean.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
          clean.setAttribute('color', new THREE.BufferAttribute(col, 3));
          parts.push(clean);
        }
      });
      if (parts.length) {
        out = parts.length === 1 ? parts[0]! : mergeGeometries(parts, false);
        if (out) addOutlineNormals(out);
      }
    }
    this.kitGeo.set(id, out);
    return out;
  }

  /** Object node from a KayKit character file (weapons live under the hand slots). */
  node(file: string, name: string): THREE.Object3D | null {
    return this.gltf.get(file)?.scene.getObjectByName(name) ?? null;
  }

  /** Borrow a VRM instance for one renderer. Parses another copy when every instance is busy. */
  async acquireVrm(file: string): Promise<VrmSlot | null> {
    const list = this.vrms.get(file) ?? [];
    const free = list.find((s) => !s.inUse);
    if (free) {
      free.inUse = true;
      return free;
    }
    const buf = this.vrmBuffers.get(file);
    if (!buf) return null;
    const slot = await this.parseVrm(file, buf);
    if (slot) slot.inUse = true;
    return slot;
  }

  /** Synchronous variant: only returns an already-parsed, free instance. */
  acquireVrmSync(file: string): VrmSlot | null {
    const free = (this.vrms.get(file) ?? []).find((s) => !s.inUse);
    if (free) free.inUse = true;
    return free ?? null;
  }

  releaseVrm(slot: VrmSlot): void {
    slot.inUse = false;
    slot.vrm.scene.removeFromParent();
  }

  async parseVrm(file: string, buf: ArrayBuffer): Promise<VrmSlot | null> {
    try {
      const gltf = await this.vrmLoader.parseAsync(buf.slice(0), '');
      const vrm = gltf.userData['vrm'] as VRM | undefined;
      if (!vrm) throw new Error('no VRM data');
      VRMUtils.removeUnnecessaryVertices(gltf.scene);
      // Merge same-material primitives while they still share their glTF skin, then let every
      // primitive of that skin use one Skeleton (one bone-matrix update + texture upload per
      // frame instead of one per primitive). Joints are not pruned: pruning gives every
      // primitive its own bone list, so nothing could share.
      const saved = mergeSkinnedParts(vrm.scene);
      shareSkeletons(vrm.scene);
      VRMUtils.rotateVRM0(vrm);
      if (saved) console.info(`[toon] ${file}: merged ${saved} primitives`);
      vrm.scene.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) {
          o.castShadow = true;
          o.receiveShadow = true;
          o.frustumCulled = false;
        }
      });
      const slot: VrmSlot = { vrm, inUse: false, prepared: false, hulls: [], side: 1, fz: 1, rightHand: null, weaponHolders: [], fist: { left: 0, right: 0 } };
      const list = this.vrms.get(file) ?? [];
      list.push(slot);
      this.vrms.set(file, list);
      return slot;
    } catch (err) {
      console.warn(`[toon] VRM ${file} failed to parse; that hero uses a placeholder capsule.`, err);
      this.failed.add(`vrm:${file}`);
      return null;
    }
  }
}

/**
 * GLTFLoader (and SkeletonUtils.clone) give every skinned primitive its own Skeleton even when
 * they use the same bones. three.js updates and uploads each Skeleton's bone texture every
 * frame, so rebind primitives with identical bones + inverse bind matrices to one Skeleton.
 * Returns how many Skeletons were dropped.
 */
export function shareSkeletons(root: THREE.Object3D): number {
  const shared: THREE.Skeleton[] = [];
  let dropped = 0;
  const same = (a: THREE.Skeleton, b: THREE.Skeleton): boolean =>
    a.bones.length === b.bones.length && a.bones.every((x, i) => x === b.bones[i]) && a.boneInverses.every((m, i) => m.equals(b.boneInverses[i]!));
  root.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh || !m.skeleton) return;
    const hit = shared.find((s) => s === m.skeleton || same(s, m.skeleton));
    if (!hit) {
      shared.push(m.skeleton);
      return;
    }
    if (hit === m.skeleton) return;
    m.skeleton.dispose();
    m.bind(hit, m.bindMatrix);
    dropped++;
  });
  return dropped;
}

/**
 * KayKit characters ship as ~9 skinned parts on one skeleton. Merge parts that share a
 * material into one SkinnedMesh (≈9 → 2 draw calls per mob, before outlines and shadows).
 */
export function mergeSkinnedParts(root: THREE.Object3D): number {
  const groups = new Map<string, THREE.SkinnedMesh[]>();
  root.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    // Morph-target meshes (VRM faces) stay separate: expressions bind to them.
    if (Object.keys(m.geometry.morphAttributes).length > 0) return;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    // GLTFLoader gives every primitive its own Skeleton object; key on the bone list instead.
    const k = `${mats.map((x) => x.uuid).join(',')}|${m.skeleton.bones.length}:${m.skeleton.bones[0]?.uuid}|${m.parent?.uuid}`;
    groups.set(k, [...(groups.get(k) ?? []), m]);
  });
  let removed = 0;
  for (const parts of groups.values()) {
    if (parts.length < 2) continue;
    const first = parts[0]!;
    const sameSkin = (p: THREE.SkinnedMesh): boolean =>
      p.skeleton.bones.every((b, i) => b === first.skeleton.bones[i]) && p.skeleton.boneInverses.every((m, i) => m.equals(first.skeleton.boneInverses[i]!));
    if (!parts.every((p) => p.bindMatrix.equals(first.bindMatrix) && p.matrix.equals(first.matrix) && sameSkin(p))) continue;
    const geos = parts.map((p) => {
      const g = p.geometry.clone();
      g.clearGroups();
      return g;
    });
    const merged = mergeGeometries(geos, false);
    geos.forEach((g) => g.dispose());
    if (!merged) continue;
    // Array materials (MToon surface + outline) draw the whole geometry once per material.
    if (Array.isArray(first.material)) {
      const n = merged.index ? merged.index.count : merged.attributes['position']!.count;
      first.material.forEach((_, i) => merged.addGroup(0, n, i));
    }
    const sm = new THREE.SkinnedMesh(merged, first.material);
    sm.name = `${first.name}_merged`;
    sm.position.copy(first.position);
    sm.quaternion.copy(first.quaternion);
    sm.scale.copy(first.scale);
    sm.castShadow = first.castShadow;
    sm.receiveShadow = first.receiveShadow;
    sm.frustumCulled = first.frustumCulled;
    sm.renderOrder = first.renderOrder;
    first.parent!.add(sm);
    sm.bind(first.skeleton, first.bindMatrix);
    for (const p of parts) p.removeFromParent();
    removed += parts.length - 1;
  }
  return removed;
}

/** Prop atlas layout: two 1024 cells side by side, white strip below. */
const ATLAS_CELL = 1024;
const ATLAS_W = 2048;
const ATLAS_H = 1088;
/** UV of a white texel (flat-coloured and procedural props). */
export const ATLAS_WHITE: readonly [number, number] = [0.5, (ATLAS_CELL + 32) / ATLAS_H];

function kitImage(g: GLTF | undefined): CanvasImageSource | null {
  let img: CanvasImageSource | null = null;
  g?.scene.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
    if (!img && m && !Array.isArray(m) && m.map?.image) img = m.map.image as CanvasImageSource;
  });
  return img;
}

/**
 * Kenney Nature colours are teal-green / blue-grey; pull them toward jungle greens and warm
 * stone so they sit with the sandstone (art bible §9: "expect to retint it").
 */
const KENNEY_RECOLOR: Record<string, number> = {
  grass: 0x4c6e30,
  leafsGreen: 0x41692c,
  leafsDark: 0x2c4f2a,
  stone: 0x9a9080,
  stoneDark: 0x6e6658,
  colorRed: 0x7d2433,
  colorTan: 0x9a7a52,
  wood: 0x7a5434,
  woodBark: 0x5e4430,
  woodBarkDark: 0x4a3626,
  woodInner: 0xb89a70,
};
function recolorKenney(name: string, c: THREE.Color): void {
  const hex = KENNEY_RECOLOR[name];
  if (hex !== undefined) c.setHex(hex);
}

export type Progress = (done: number, total: number, label: string) => void;

let loading: Promise<ToonAssets> | null = null;

/** Load everything once. Resolves even when some files fail (they're listed in `failed`). */
export function loadToonAssets(onProgress?: Progress): Promise<ToonAssets> {
  if (loading) return loading;
  loading = (async () => {
    const lib = new ToonAssets();
    const gltfLoader = new GLTFLoader();
    const kitEntries = Object.entries(VENOM_KITS);
    const total = KAYKIT_FILES.length + VRM_FILES.length * 2 + kitEntries.length + VENOM_TEXTURES.length;
    let done = 0;
    const tick = (label: string): void => {
      done++;
      onProgress?.(done, total, label);
    };
    onProgress?.(0, total, 'Loading models');
    const glbs = KAYKIT_FILES.map(async (f) => {
      try {
        const g = await gltfLoader.loadAsync(`${base()}assets/kaykit/${f}.glb`);
        if (g.animations.length) mergeSkinnedParts(g.scene); // +field: adventurers too (≈6 → 2 draws)
        lib.gltf.set(f, g);
      } catch (err) {
        lib.failed.add(f);
        console.warn(`[toon] model ${f} failed to load; using a fallback.`, err);
      }
      tick(f);
    });
    const vrms = VRM_FILES.map(async (f) => {
      try {
        const res = await fetch(`${base()}assets/vrm/${f}.vrm`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        lib.vrmBuffers.set(f, await res.arrayBuffer());
      } catch (err) {
        lib.failed.add(`vrm:${f}`);
        console.warn(`[toon] hero ${f} failed to download; using a placeholder capsule.`, err);
      }
      tick(`${f}.vrm`);
    });
    const kits = kitEntries.map(async ([key, file]) => {
      try {
        lib.kits.set(key, await gltfLoader.loadAsync(`${base()}${file}`));
      } catch (err) {
        lib.failed.add(`kit:${key}`);
        console.warn(`[toon] theme kit ${key} failed to load; its props are skipped.`, err);
      }
      tick(`kit ${key}`);
    });
    const texLoader = new THREE.TextureLoader();
    const texs = VENOM_TEXTURES.map(async (t) => {
      try {
        const tex = await texLoader.loadAsync(`${base()}${t.files[1024]}`);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.anisotropy = 8;
        tex.name = t.id;
        lib.textures.set(t.id, tex);
      } catch (err) {
        lib.failed.add(`tex:${t.id}`);
        console.warn(`[toon] texture ${t.id} failed to load; that surface uses a flat colour.`, err);
      }
      tick(t.id);
    });
    await Promise.all([...glbs, ...vrms, ...kits, ...texs]);
    // Parse VRMs one at a time (texture decode is heavy) so the progress bar keeps moving.
    for (const f of VRM_FILES) {
      const buf = lib.vrmBuffers.get(f);
      if (buf) await lib.parseVrm(f, buf);
      tick(`hero ${f}`);
    }
    return lib;
  })();
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/** Already-loaded library, if any (sync access for renderer hot-swaps). */
export function loadedToonAssets(): Promise<ToonAssets> | null {
  return loading;
}
